import {
  frameSize,
  type AspectRatio,
  type FrameSize,
  type QualityPreset,
  type ResolutionName,
} from '../render/RenderSettings.js';
import type { ContainerFormat } from './AudioExport.js';

/**
 * What the export panel is asking for, and what that means in numbers.
 *
 * The panel itself is DOM and lives in `src/hud/`. Everything it has to *decide*
 * is here instead: which preset means what, how a resolution name and an aspect
 * become pixels, what the file will be called, which phase a progress report
 * represents, and what to tell somebody when it fails. None of that needs a
 * browser, and all of it is the part worth testing.
 *
 * ## Why the configuration is a snapshot
 *
 * An export runs for minutes. The panel stays on screen the whole time, and
 * nothing stops a user changing the frame rate halfway through — so the
 * configuration is copied once, at the moment START is pressed, and the running
 * export reads only the copy. Without that the render loop and the muxer can
 * disagree about how long a frame is, and the disagreement shows up as a file
 * whose audio drifts out of sync with no error anywhere.
 */

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

/** Which interpretation of the performance to export. */
export type ExportRecipeChoice = 'original' | 'current' | 'randomize';

/** What the user asked of the codec picker. */
export const CODEC_CHOICES = ['AUTO', 'H.264', 'VP9', 'AV1'] as const;
export type CodecChoice = (typeof CODEC_CHOICES)[number];

export interface ExportConfig {
  readonly resolution: ResolutionName;
  readonly aspect: AspectRatio;
  readonly fps: number;
  readonly quality: QualityPreset;
  readonly codec: CodecChoice;
  readonly audio: boolean;
  readonly recipe: ExportRecipeChoice;
}

export const EXPORT_FPS = [24, 30, 60] as const;
export type ExportFps = (typeof EXPORT_FPS)[number];

export const DEFAULT_EXPORT_CONFIG: ExportConfig = {
  resolution: '1080p',
  aspect: '16:9',
  fps: 60,
  quality: 'HIGH',
  codec: 'AUTO',
  audio: true,
  recipe: 'original',
};

/**
 * Copies a configuration so a running export cannot be edited underneath it.
 *
 * A plain spread, and that is enough because every field is a primitive — but it
 * is a named function rather than an inline `{...config}` so the reason survives
 * in the call site. See the note at the top of this file.
 */
export function snapshotConfig(config: ExportConfig): ExportConfig {
  return {
    resolution: config.resolution,
    aspect: config.aspect,
    fps: config.fps,
    quality: config.quality,
    codec: config.codec,
    audio: config.audio,
    recipe: config.recipe,
  };
}

// ---------------------------------------------------------------------------
// Presets
// ---------------------------------------------------------------------------

export interface ExportPreset {
  readonly id: string;
  readonly label: string;
  /** One line, for the panel. */
  readonly note: string;
  readonly config: ExportConfig;
}

/**
 * The export presets.
 *
 * These are **not** the visual art presets. `INK RITUAL` decides what the piece
 * looks like; `VERTICAL` decides what shape the file is. The two are chosen
 * separately and combine freely, which is the whole point — the same artwork
 * recipe should be exportable as a widescreen film and as a phone-shaped clip
 * without either choice disturbing the other.
 */
export const EXPORT_PRESETS: readonly ExportPreset[] = [
  {
    id: 'STANDARD',
    label: 'STANDARD',
    note: '1080p · 16:9 · 60fps',
    config: { ...DEFAULT_EXPORT_CONFIG },
  },
  {
    id: 'VERTICAL',
    label: 'VERTICAL',
    note: '1080×1920 · 9:16 · 60fps',
    config: { ...DEFAULT_EXPORT_CONFIG, aspect: '9:16' },
  },
  {
    id: 'SQUARE',
    label: 'SQUARE',
    note: '1:1 · 30fps',
    config: { ...DEFAULT_EXPORT_CONFIG, aspect: '1:1', fps: 30 },
  },
  {
    id: 'ULTRA',
    label: 'ULTRA',
    note: '4K · 16:9 · 60fps · maximum quality',
    config: { ...DEFAULT_EXPORT_CONFIG, resolution: '4K', quality: 'ULTRA' },
  },
];

export function presetById(id: string): ExportPreset | null {
  return EXPORT_PRESETS.find((preset) => preset.id === id) ?? null;
}

/** Which preset a configuration matches exactly, if any. */
export function matchingPreset(config: ExportConfig): ExportPreset | null {
  return (
    EXPORT_PRESETS.find(
      (preset) =>
        preset.config.resolution === config.resolution &&
        preset.config.aspect === config.aspect &&
        preset.config.fps === config.fps &&
        preset.config.quality === config.quality,
    ) ?? null
  );
}

// ---------------------------------------------------------------------------
// What the numbers mean
// ---------------------------------------------------------------------------

export interface ExportPlan {
  readonly size: FrameSize;
  readonly frames: number;
  readonly durationSeconds: number;
  /** `1920 × 1080`, ready to show. */
  readonly sizeLabel: string;
}

/**
 * The output size, spelled out.
 *
 * Worth showing rather than implying. The resolution names are long-edge
 * measurements — `1080p` means "the long side is 1920" — so `1080p` with a 1:1
 * aspect is a 1920×1920 frame, not 1080×1080, and `1080p` vertical is 1080×1920.
 * Nobody guesses that from the name, and a user who expected a 1080-pixel frame
 * and got a 1920-pixel one finds out when the export takes four times as long.
 */
export function planExport(
  config: ExportConfig,
  songDurationSeconds: number,
): ExportPlan {
  const size = frameSize(config.resolution, config.aspect);
  const duration = Number.isFinite(songDurationSeconds) && songDurationSeconds > 0
    ? songDurationSeconds
    : 0;
  // The same rule `OfflineRenderClock` uses: enough frames to cover the song,
  // counting the frame at time zero.
  const frames = duration > 0 ? Math.floor(duration * config.fps) + 1 : 0;
  return {
    size,
    frames,
    durationSeconds: duration,
    sizeLabel: `${size.width} × ${size.height}`,
  };
}

// ---------------------------------------------------------------------------
// Phases
// ---------------------------------------------------------------------------

export const EXPORT_PHASES = [
  'idle',
  'preparing',
  'rendering',
  'flushing',
  'muxing',
  'done',
  'cancelled',
  'failed',
] as const;
export type ExportPhase = (typeof EXPORT_PHASES)[number];

const PHASE_LABELS: Readonly<Record<ExportPhase, string>> = {
  idle: 'Ready',
  preparing: 'Preparing',
  rendering: 'Rendering & encoding',
  flushing: 'Encoding audio',
  muxing: 'Muxing',
  done: 'Done',
  cancelled: 'Cancelled',
  failed: 'Failed',
};

export function phaseLabel(phase: ExportPhase): string {
  return PHASE_LABELS[phase];
}

/**
 * The export's own state, as a phase worth showing.
 *
 * `AvExportSession` reports `exporting` for the long middle of the job, during
 * which video and audio are encoded together — so there is no separate "encoding
 * video" step to show, and inventing one would be a lie about what is happening.
 * What the panel gets instead is the honest set.
 */
export function phaseFor(state: string): ExportPhase {
  switch (state) {
    case 'configuring':
      return 'preparing';
    case 'exporting':
      return 'rendering';
    case 'flushing':
      return 'flushing';
    case 'finalizing':
      return 'muxing';
    case 'finished':
      return 'done';
    case 'cancelled':
      return 'cancelled';
    case 'failed':
      return 'failed';
    default:
      return 'idle';
  }
}

// ---------------------------------------------------------------------------
// Naming
// ---------------------------------------------------------------------------

/**
 * A file name that will survive being saved.
 *
 * The song's title is kept because that is what makes the file findable later,
 * with only the characters a filesystem actually rejects replaced — the same
 * rule the song pack exporter already follows, so a Japanese title stays
 * readable rather than becoming a row of underscores.
 *
 * The timestamp is local and sortable, and it is what stops the second export
 * of a song silently replacing the first in a downloads folder.
 */
export function exportFileName(options: {
  readonly song: string;
  readonly container: ContainerFormat;
  readonly at: Date;
  /** Appended when the artwork is not the performance's own. */
  readonly recipeLabel?: string;
}): string {
  const song = sanitiseNamePart(options.song) || 'mandalizm';
  const recipe = options.recipeLabel === undefined ? '' : sanitiseNamePart(options.recipeLabel);
  const stamp = timeStamp(options.at);
  const middle = recipe.length > 0 ? `${song}_${recipe}` : song;
  return `mandalizm_${middle}_${stamp}.${options.container}`;
}

function sanitiseNamePart(value: string): string {
  return (
    value
      // Exactly what a filesystem refuses, plus the separators a name must not
      // contain. Everything else — including non-Latin scripts — is kept.
      // eslint-disable-next-line no-control-regex
      .replace(/[<>:"/\\|?* -]/g, '')
      .replace(/\s+/g, '-')
      .replace(/^[.\-]+|[.\-]+$/g, '')
      .slice(0, 60)
  );
}

function timeStamp(at: Date): string {
  const pad = (value: number): string => String(value).padStart(2, '0');
  return (
    `${at.getFullYear()}${pad(at.getMonth() + 1)}${pad(at.getDate())}` +
    `_${pad(at.getHours())}${pad(at.getMinutes())}${pad(at.getSeconds())}`
  );
}

/** `84.3 MB`, or `912 KB` when that reads better. */
export function fileSizeLabel(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  if (bytes < 1024) return `${Math.round(bytes)} B`;
  if (bytes < 1_000_000) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1_000_000).toFixed(1)} MB`;
}

/** `2:34`, for a duration a person is going to read. */
export function durationLabel(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return '0.00s';
  return `${seconds.toFixed(2)}s`;
}

// ---------------------------------------------------------------------------
// Failure
// ---------------------------------------------------------------------------

export type ExportFailureKind =
  | 'no-webcodecs'
  | 'no-audio-encoder'
  | 'no-codec'
  | 'no-audio-codec'
  | 'incompatible'
  | 'too-large'
  | 'invalid-pcm'
  | 'no-replay'
  | 'video-encode'
  | 'audio-encode'
  | 'mux'
  | 'cancelled'
  | 'unknown';

/**
 * What to tell somebody when an export does not happen.
 *
 * Each of these is a different thing to do next, which is the only reason to
 * distinguish them: an unsupported codec means change a setting, a GPU limit
 * means choose a smaller size, a missing replay means play something first. A
 * single "export failed" would be true and useless, and putting the detail in
 * the console would put it where the person who needs it will not look.
 */
export function describeFailure(kind: ExportFailureKind, detail?: string): string {
  const base = FAILURE_TEXT[kind];
  if (detail === undefined || detail.length === 0) return base;
  return `${base} (${detail})`;
}

const FAILURE_TEXT: Readonly<Record<ExportFailureKind, string>> = {
  'no-webcodecs':
    'This browser cannot encode video. Production export needs WebCodecs — try a recent Chrome or Edge.',
  'no-audio-encoder':
    'This browser cannot encode audio. Turn Audio off to export video only.',
  'no-codec': 'No video codec on this machine can encode that size and frame rate.',
  'no-audio-codec': 'No audio codec fits this container at the song’s sample rate.',
  incompatible: 'The chosen video and audio codecs cannot share one container.',
  'too-large': 'That frame size is larger than this GPU allows.',
  'invalid-pcm': 'The song’s audio could not be read.',
  'no-replay': 'Play or load a performance first — there is nothing to export.',
  'video-encode': 'The video encoder failed part-way through.',
  'audio-encode': 'The audio encoder failed part-way through.',
  mux: 'The file could not be assembled.',
  cancelled: 'Export cancelled.',
  unknown: 'The export stopped for an unknown reason.',
};

/**
 * The line shown when AUTO had to change format.
 *
 * A fallback that happens quietly is the worst of both: the export succeeds, the
 * file is a WebM, and the user finds out when something refuses to open it. This
 * is the sentence that stops that, and it names both halves — what was wanted
 * and what is being used instead.
 *
 * Audio counts as well as video, and that is not a detail. The first UI export
 * run on this machine produced an MP4 with an Opus soundtrack, because the song
 * is mono and this browser refuses AAC at one channel. The pipeline chose
 * correctly and said nothing, which is precisely the silence this exists to
 * break — an MP4 carrying Opus is legal and is still refused by a good number of
 * players.
 */
export function fallbackNotice(options: {
  readonly wantedCodec: string;
  readonly usedCodec: string;
  readonly usedContainer: ContainerFormat;
  readonly size: FrameSize;
  readonly wantedAudioCodec?: string | null;
  readonly usedAudioCodec?: string | null;
}): string | null {
  const videoChanged = options.wantedCodec !== options.usedCodec;
  const wantedAudio = options.wantedAudioCodec ?? null;
  const usedAudio = options.usedAudioCodec ?? null;
  const audioChanged = wantedAudio !== null && usedAudio !== null && wantedAudio !== usedAudio;
  if (!videoChanged && !audioChanged) return null;

  const parts: string[] = [];
  if (videoChanged) {
    parts.push(
      `${options.wantedCodec} is unavailable at ${options.size.width}×${options.size.height}`,
    );
  }
  if (audioChanged) {
    parts.push(`${wantedAudio} is unavailable for this song's audio`);
  }
  const used = `${options.usedCodec}${usedAudio === null ? '' : ` + ${usedAudio}`} / ` +
    options.usedContainer.toUpperCase();
  return `Export format changed: ${parts.join('; ')}. Using ${used}.`;
}

/** `MP4 / H.264 / AAC-LC`, or `WebM / VP9 / no audio`. */
export function formatSummary(options: {
  readonly container: ContainerFormat;
  readonly videoCodec: string;
  readonly audioCodec: string | null;
}): string {
  const container = options.container.toUpperCase() === 'MP4' ? 'MP4' : 'WebM';
  return `${container} / ${options.videoCodec} / ${options.audioCodec ?? 'no audio'}`;
}
